import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import mime from 'mime';
import { PDFSource, PdfViewerComponent } from 'ng2-pdf-viewer';
import { APPTYPES } from 'src/app/share/constants/common-constants';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-view-document',
    templateUrl: './view-document.component.html',
    styleUrls: ['./view-document.component.scss'],
    standalone: false
})
export class ViewDocumentComponent implements AfterViewInit, OnInit {
    @Input() viewDetails: any;
    @Input() selectedFile: any;
    @Input() pdfSrc: string | Uint8Array | PDFSource;
    @Input() viewEsign: boolean;
    @Input() searchOption: any;
    @ViewChild('fileContainer') fileContainer: ElementRef;
    @ViewChild(PdfViewerComponent, { static: false }) private readonly pdfViewer: PdfViewerComponent;
    @Output() fileActionEvent: EventEmitter<any> = new EventEmitter<any>();

    envType = environment?.envType || 'SDEX';
    appType = APPTYPES;
    faDownload = faDownload;
    noPreview = false;
    isSearch: boolean = false;
    searchText: any = [];
    searchControl: FormControl;
    downloadLoader: boolean = false;
    matchCount: number = 0;
    currentMatchIndex: number = 0;
    eventBus: any;
    searchStatus = 'Idle';
    searchReady = false;
    searchState: number = -1;
    mime = mime;
    fileExtension: any;
    fileTypes = ['pdf', 'jpeg', 'png', 'txt'];


    constructor(public modal: NgbActiveModal, private readonly sanitizer: DomSanitizer) {
        this.searchControl = new FormControl('');
    }

    ngOnInit(): void {
        this.fileExtension = this.mime.getExtension(
            this.viewDetails?.file?.mimeType || this.selectedFile?.mimeType || ''
        );
    }

    docUrl(url: any): any {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    onSearch(event: any) {
        let searchText = this.searchControl.value?.trim();
        if (this.searchOption === 'exact') {
            this.searchText = searchText;
        } else {
            this.searchText = searchText?.split(' ');
        }
        this.pdfSearchTextHighlighted();
    }

    pdfSearchTextHighlighted() {
        let mimeType = this.viewDetails?.file?.mimeType || this.selectedFile?.mimeType || '';
        if (mimeType?.toLowerCase().includes('pdf') || mimeType?.toLowerCase().includes('application/octet-stream')) {
            this.searchReady = false;
            this.matchCount = 0;
            this.currentMatchIndex = 0;
            this.searchStatus = '🔍 Searching...';

            this.pdfViewer?.pdfFindController._eventBus.dispatch('find', {
                query: this.searchText,
                type: '',
                caseSensitive: false,
                findPrevious: undefined,
                highlightAll: true,
                phraseSearch: true,
            });
            this.noPreview = false;
        } else if (mimeType?.toLowerCase().includes('image')) {
            this.noPreview = false;
        } else {
            this.noPreview = true;
        }
    }

    ngAfterViewInit() {
    if (this.viewDetails) {
        let files = new Blob([this.viewDetails?.data?.body], {
            type: this.viewDetails?.file?.mimeType
        });
        let url = URL.createObjectURL(files);
        if (this.viewDetails?.file?.mimeType?.includes('image')) {
            let _image = document.createElement('img');
            _image.setAttribute('src', url);
            _image.style.width = '100%';
            _image.style.maxHeight = '800px';
            _image.style.objectFit = 'contain';
            this.fileContainer.nativeElement.append(_image);
        } else if (this.viewDetails?.file?.mimeType?.includes('pdf') && !this.isSearch) {
            let _iFrame = document.createElement('iframe');
            _iFrame.setAttribute('src', url);
            _iFrame.setAttribute('scrolling', 'auto');
            _iFrame.setAttribute('height', '800');
            _iFrame.setAttribute('width', '100%');
            this.fileContainer.nativeElement.append(_iFrame);
        } else if (!this.isSearch) {
            this.noPreview = true;
        }
        }

        if (this.searchText) {
            setTimeout(() => {
                this.eventBus = (this.pdfViewer as any)?.pdfFindController?._eventBus;
                if (this.eventBus) {
                    this.listenToSearchEvents();
                } else {
                    console.error('EventBus not found.');
                }
            }, 500);
        }
    }

    download() {
        const link = document.createElement('a');
        link.href = globalThis.URL.createObjectURL(this.viewDetails?.data?.body);
        link.download = this.viewDetails?.file?.name;
        document.body.appendChild(link);
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: globalThis as Window & typeof globalThis }));
        link.remove();
        globalThis.URL.revokeObjectURL(link.href);
    }

    clearSearch() {
        this.searchControl.reset();
        this.searchText = '';
        this.pdfSearchTextHighlighted();
    }

    downloadFile() {
        this.downloadLoader = true;
        this.fileActionEvent.next({ 
            actionType: 'download',
            file: this.selectedFile,
            fileId: this.selectedFile?._id
        })
        setTimeout(() => {
            this.downloadLoader = false;
        }, 3000)
    }

    goToNextMatch() {

        this.pdfViewer?.pdfFindController._eventBus.dispatch('find', {
            query: this.searchText,
            type: 'again',
            caseSensitive: false,
            findPrevious: false,
            highlightAll: true,
            phraseSearch: true,
        });
    }

    goToPreviousMatch() {
        if (this.currentMatchIndex === 1) {
            return;
        }

        this.pdfViewer?.pdfFindController._eventBus.dispatch('find', {
            query: this.searchText,
            type: 'again',
            caseSensitive: false,
            findPrevious: true,
            highlightAll: true,
            phraseSearch: true,
        });
    }

    private listenToSearchEvents(): void {
        // Step 1: Track status and current index
        this.eventBus.on('updatefindcontrolstate', (e: any) => {
            const state = e.state;

            // Just update current match index
            if (typeof e.matchesCount?.current === 'number') {
                this.currentMatchIndex = e.matchesCount.current;
            }

            // Save state to determine later if complete
            this.searchState = state;

            if (state === 1) {
                this.searchStatus = '🔍 Searching...';
                this.searchReady = false;
            }
        });

        // Step 2: When match count is updated (guaranteed to come AFTER state 0 or 4), finalize status
        this.eventBus.on('updatefindmatchescount', (e: any) => {
            const total = e.matchesCount?.total;
            if (typeof total === 'number') {
                this.matchCount = total;
                if (this.currentMatchIndex === 0) {
                    this.currentMatchIndex = 1;
                }

                if (this.searchState === 0 || this.searchState === 4) {
                    if (this.matchCount > 0) {
                        this.searchReady = true;
                    }
                }
            }
        });
    }

    scrollToBottomAndTop(): void {
        setTimeout(() => {
            const container = document.querySelector('.ng2-pdf-viewer-container');
            if (container) {
                container.scrollTop = container.scrollHeight; // Scroll to bottom
                setTimeout(() => {
                    container.scrollTop = 0; // Scroll back to top
                }, 500);
            }
        }, 1000);
    }

    onFileSelected(event: any): void {
        const file: File = event.target.files[0];
        if (file?.type === 'application/pdf') {
            const reader = new FileReader();
            reader.onload = () => {
                this.pdfSrc = new Uint8Array(reader.result as ArrayBuffer);
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert('Please select a valid PDF file.');
        }
    }



}
